import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { applyFieldErrors } from '@nucleus-commerce/admin-kit/errors';
import type { ChatAgent, ChatDepartment } from '@nucleus-commerce/api-client/admin';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Checkbox } from '@nucleus-commerce/ui/components/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@nucleus-commerce/ui/components/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Label } from '@nucleus-commerce/ui/components/label';

import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import {
  emptyDepartment,
  fromDepartment,
  toCreateDepartment,
  toUpdateDepartment,
} from '../api/mappers';
import { useDepartmentMutations } from '../hooks/use-department-mutations';
import { useServerMessage } from '../hooks/use-server-message';
import { departmentSchema, type DepartmentForm } from '../schemas/department-schema';

import { FormAlert, SwitchField, TextField } from './form-controls';

const KNOWN_FIELDS: ReadonlySet<string> = new Set([
  'name',
  'description',
  'position',
  'isDefault',
  'memberIds',
]);

export interface DepartmentDialogProps {
  storeId: string;
  department: ChatDepartment | null;
  nextPosition: number;
  /** The store's chat agents; null while they load or when they failed, and members are then left alone. */
  agents: ChatAgent[] | null;
  onOpenChange: (open: boolean) => void;
}

export function DepartmentDialog({
  storeId,
  department,
  nextPosition,
  agents,
  onOpenChange,
}: DepartmentDialogProps) {
  const { t } = useTranslation(['chatSettings', 'common']);
  const serverMessage = useServerMessage();
  const { create, update } = useDepartmentMutations(storeId);
  const form = useForm<DepartmentForm>({
    resolver: useI18nResolver(departmentSchema),
    defaultValues: department ? fromDepartment(department) : emptyDepartment(nextPosition),
  });
  const saving = create.isPending || update.isPending;
  // A department routes chats, so only members who can reply are offered. Members already in it
  // who are not offered stay in `memberIds` untouched.
  const offered = agents?.filter((agent) => agent.canReply) ?? null;

  const submit = form.handleSubmit(async (values) => {
    form.clearErrors('root.server');
    try {
      if (department) {
        await update.mutateAsync({
          id: department.id,
          body: toUpdateDepartment(values, { includeMembers: offered !== null }),
        });
      } else {
        await create.mutateAsync(toCreateDepartment(values));
      }
      onOpenChange(false);
    } catch (error) {
      if (applyFieldErrors(error, form.setError, { knownFields: KNOWN_FIELDS })) return;
      form.setError('root.server', { type: 'server', message: serverMessage(error) });
    }
  });

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {department ? t('departments.editTitle') : t('departments.newTitle')}
          </DialogTitle>
          <DialogDescription>{t('departments.dialogDescription')}</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={submit} noValidate className="flex flex-col gap-4">
            <FormAlert message={form.formState.errors.root?.server?.message} />
            <TextField control={form.control} name="name" label={t('departments.name')} />
            <TextField
              control={form.control}
              name="description"
              label={t('departments.descriptionLabel')}
              description={t('departments.descriptionHint')}
              multiline
            />
            <TextField
              control={form.control}
              name="position"
              label={t('departments.position')}
              description={t('departments.positionHint')}
              numeric
            />
            <SwitchField
              control={form.control}
              name="isDefault"
              label={t('departments.isDefault')}
              description={t('departments.isDefaultHint')}
            />
            {offered ? (
              <FormField
                control={form.control}
                name="memberIds"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('departments.members')}</FormLabel>
                    {offered.length === 0 ? (
                      <p className="text-muted-foreground text-sm">{t('departments.noStaff')}</p>
                    ) : (
                      <ul className="max-h-56 space-y-2 overflow-y-auto rounded-md border p-3">
                        {offered.map((member) => {
                          const id = `department-member-${member.userId}`;
                          const checked = field.value.includes(member.userId);
                          return (
                            <li key={member.userId} className="flex items-center gap-2">
                              <FormControl>
                                <Checkbox
                                  id={id}
                                  checked={checked}
                                  onCheckedChange={(next) =>
                                    field.onChange(
                                      next === true
                                        ? [...field.value, member.userId]
                                        : field.value.filter((v) => v !== member.userId),
                                    )
                                  }
                                />
                              </FormControl>
                              <Label htmlFor={id} className="font-normal">
                                {member.name}
                                <span className="text-muted-foreground ml-1 text-xs">
                                  {member.role.name}
                                </span>
                              </Label>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                    <FormDescription>{t('departments.membersHint')}</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            ) : (
              <p className="text-muted-foreground text-sm">{t('departments.membersUnavailable')}</p>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                {t('common:actions.cancel')}
              </Button>
              <Button type="submit" disabled={saving}>
                {saving ? t('common:actions.saving') : t('common:actions.save')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
